import React, { useState, useEffect } from 'react';
import {
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  Chip,
  IconButton,
  Avatar,
  CircularProgress,
} from '@mui/material';
import {
  DragIndicator as DragIndicatorIcon,
  SortByAlpha as SortByAlphaIcon,
  CheckCircle as CheckCircleIcon,
  Tag as TagIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  History as HistoryIcon,
  ExpandMore as ExpandMoreIcon,
  StarOutline as StructuralOrderIcon,
  PaidOutlined as ValuationIcon,
  GroupsOutlined as AudienceIcon,
  EmojiEventsOutlined as TierIcon,
} from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { useTheme } from '../../../../design-system/providers/ThemeProvider';
import PlatformDialog from '../../../molecules/PlatformDialog/PlatformDialog';
import PlatformDialogTitle from '../../../molecules/PlatformDialog/PlatformDialogTitle';
import { getEffectiveTier } from '../../../../utils/effectiveTier';
import { parseSubscribers } from '../../../../utils/subscriberParser';

const normalizeSourceKey = (article = {}) => {
  const rawUrl = article.siteUrl || '';
  if (rawUrl) {
    try {
      const normalizedUrl = /^https?:\/\//i.test(rawUrl) ? rawUrl : `https://${rawUrl}`;
      const host = new URL(normalizedUrl).hostname.replace(/^www\./i, '').toLowerCase();
      if (host) return `domain:${host}`;
    } catch (_) {
      // Continua com os identificadores de fallback.
    }
  }

  if (article.username) return `profile:${String(article.username).trim().toLowerCase()}`;
  return `name:${String(article.siteName || 'fonte-desconhecida').trim().toLowerCase()}`;
};

const getAudienceValue = (article = {}) => {
  const candidates = [article.audienceMonthly, article.audience, article.subscribers, article.views, article.reach];
  return candidates.reduce((largest, value) => Math.max(largest, parseSubscribers(String(value || 0)) || 0), 0);
};

const buildHistoryPreferences = (history = []) => {
  const preferences = new Map();

  history.forEach((newsletter, historyIndex) => {
    const orderedSources = [];
    const seen = new Set();
    (newsletter.articles || []).forEach(article => {
      const sourceKey = normalizeSourceKey(article);
      if (!seen.has(sourceKey)) {
        seen.add(sourceKey);
        orderedSources.push(sourceKey);
      }
    });

    const recencyWeight = Math.max(1, history.length - historyIndex);
    for (let first = 0; first < orderedSources.length; first += 1) {
      for (let second = first + 1; second < orderedSources.length; second += 1) {
        const preferred = orderedSources[first];
        const secondary = orderedSources[second];
        preferences.set(`${preferred}\u0000${secondary}`, (preferences.get(`${preferred}\u0000${secondary}`) || 0) + recencyWeight);
        preferences.set(`${secondary}\u0000${preferred}`, (preferences.get(`${secondary}\u0000${preferred}`) || 0) - recencyWeight);
      }
    }
  });

  return preferences;
};

/**
 * NewsletterOrderDialog
 *
 * Modal de gabarito de envio da newsletter em 2 camadas:
 * 1. Ordem das Tags/Classificações (drag & drop)
 * 2. Critério de ordenação dos artigos dentro de cada grupo
 */
const NewsletterOrderDialog = ({
  open,
  onClose,
  articles = [],
  globalTags = [],
  initialTemplate = null,
  onApply,
  orderHistory = [],
  orderHistoryLoading = false,
  sourceOrder = []
}) => {
  const { 
    getPrimaryBackground, 
    isDarkMode, 
    isPrimaryGradient 
  } = useTheme();
  const [orderedTags, setOrderedTags] = useState([]);
  const [sortCriteria, setSortCriteria] = useState('valuation');
  const [showTagOrder, setShowTagOrder] = useState(false);

  useEffect(() => {
    if (open) {
      setShowTagOrder(false);
      // Comecar com as tags globais da marca
      let tagsToDisplay = globalTags.map(t => ({ id: t._id, name: t.name }));

      // Se ja existe um gabarito salvo, ordenar por ele
      if (initialTemplate && initialTemplate.tagsOrder && initialTemplate.tagsOrder.length > 0) {
        const savedOrder = initialTemplate.tagsOrder;
        tagsToDisplay.sort((a, b) => {
          const indexA = savedOrder.indexOf(a.name);
          const indexB = savedOrder.indexOf(b.name);
          if (indexA === -1 && indexB === -1) return 0;
          if (indexA === -1) return 1; // Tags novas pro fim
          if (indexB === -1) return -1;
          return indexA - indexB;
        });
      }

      // Sempre adicionar a categoria "Sem Classificacao" no final para capturar os sem tag
      tagsToDisplay.push({ id: '__no_tag__', name: 'Sem Classificação' });

      setOrderedTags(tagsToDisplay);

      if (initialTemplate && initialTemplate.sortCriteria) {
        setSortCriteria(initialTemplate.sortCriteria);
      }
    }
  }, [open, globalTags, initialTemplate]);

  // Funcao local de contagem apenas para mostrar no chip visualmente
  const getArticleCount = (id, name) => {
    if (id === '__no_tag__') {
      return articles.filter(a => !a.tags || a.tags.length === 0).length;
    }
    return articles.filter(a =>
      (a.tags || []).some(t =>
        (typeof t === 'object' ? (t._id === id || t.name === name) : t === name)
      )
    ).length;
  };

  // Reordenação por drag
  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const items = Array.from(orderedTags);
    const [moved] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, moved);
    setOrderedTags(items);
  };

  // Mover para cima/baixo via botões
  const moveTag = (index, direction) => {
    const items = Array.from(orderedTags);
    const swapIndex = index + direction;
    if (swapIndex < 0 || swapIndex >= items.length) return;
    [items[index], items[swapIndex]] = [items[swapIndex], items[index]];
    setOrderedTags(items);
  };

  // Aplicar gabarito e devolver artigos reordenados para o pai
  const handleApply = () => {
    const historyPreferences = buildHistoryPreferences(orderHistory);
    const structuralRanks = new Map(
      [...sourceOrder]
        .sort((a, b) => a.position - b.position)
        .map((source, index) => [source.sourceKey, index])
    );
    const sourceStats = new Map();

    articles.forEach(article => {
      const sourceKey = normalizeSourceKey(article);
      const current = sourceStats.get(sourceKey) || { audience: 0, tier: 9, valuation: 0, date: 0, name: article.siteName || '' };
      sourceStats.set(sourceKey, {
        audience: Math.max(current.audience, getAudienceValue(article)),
        tier: Math.min(current.tier, getEffectiveTier(article) || 9),
        valuation: Math.max(current.valuation, Number(article.valuation) || 0),
        date: Math.max(current.date, new Date(article.date || article.createdAt || 0).getTime() || 0),
        name: current.name || article.siteName || ''
      });
    });

    const compareByRelevance = (firstArticle, secondArticle) => {
      const firstSource = normalizeSourceKey(firstArticle);
      const secondSource = normalizeSourceKey(secondArticle);
      const firstStats = sourceStats.get(firstSource);
      const secondStats = sourceStats.get(secondSource);

      if (firstSource !== secondSource) {
        const historicalPreference = historyPreferences.get(`${firstSource}\u0000${secondSource}`) || 0;
        if (historicalPreference !== 0) return historicalPreference > 0 ? -1 : 1;
      }

      if (firstStats.audience !== secondStats.audience) return secondStats.audience - firstStats.audience;
      if (firstStats.tier !== secondStats.tier) return firstStats.tier - secondStats.tier;
      if (firstStats.valuation !== secondStats.valuation) return secondStats.valuation - firstStats.valuation;
      if (firstStats.date !== secondStats.date) return secondStats.date - firstStats.date;
      return firstStats.name.localeCompare(secondStats.name, 'pt-BR');
    };

    const compareByStructure = (firstArticle, secondArticle) => {
      const firstSource = normalizeSourceKey(firstArticle);
      const secondSource = normalizeSourceKey(secondArticle);
      const firstRank = structuralRanks.get(firstSource);
      const secondRank = structuralRanks.get(secondSource);
      if (firstRank !== undefined && secondRank !== undefined && firstRank !== secondRank) return firstRank - secondRank;
      if (firstRank !== undefined && secondRank === undefined) return -1;
      if (firstRank === undefined && secondRank !== undefined) return 1;
      return compareByRelevance(firstArticle, secondArticle);
    };

    // Função de valor para ordenação dos artigos por critério
    const getSortValue = (article) => {
      switch (sortCriteria) {
        case 'valuation':
          return parseFloat(article.valuation) || 0;
        case 'audience':
          return parseFloat(article.audience) || parseFloat(article.subscribers) || 0;
        case 'tier': {
          const tier = getEffectiveTier(article);
          return tier ? 4 - tier : 0;
        }
        default:
          return 0;
      }
    };

    // Montar array final com artigos agrupados pela ordem das tags
    const seenIds = new Set();
    const reordered = [];

    orderedTags.forEach(({ id, name }) => {
      let group;
      if (id === '__no_tag__') {
        group = articles.filter(a => !a.tags || a.tags.length === 0);
      } else {
        group = articles.filter(a =>
          (a.tags || []).some(t =>
            (typeof t === 'object' ? (t._id === id || t.name === name) : t === name)
          )
        );
      }
      // Ordenar dentro do grupo pelo critério escolhido (maior → menor)
      group.sort((a, b) => {
        if (sortCriteria === 'structural') return compareByStructure(a, b);
        if (sortCriteria === 'history') return compareByRelevance(a, b);
        return getSortValue(b) - getSortValue(a);
      });

      group.forEach(article => {
        if (!seenIds.has(article._id)) {
          seenIds.add(article._id);
          reordered.push(article);
        }
      });
    });

    articles.forEach(a => {
      if (!seenIds.has(a._id)) {
        reordered.push(a);
      }
    });

    onApply(reordered, {
      orderedTags: orderedTags.map(t => t.name),
      sortCriteria,
      shouldPersist: !['history', 'structural'].includes(sortCriteria)
    });
  };

  const orderingOptions = [
    {
      value: 'structural',
      title: 'Ordem estrutural da marca',
      description: sourceOrder.length > 0
        ? `Mantém ${sourceOrder.length} fontes nas posições aprendidas`
        : 'Faça um ajuste manual para ensinar a primeira estrutura',
      icon: <StructuralOrderIcon />,
      recommended: true,
      disabled: sourceOrder.length === 0
    },
    {
      value: 'history',
      title: 'Ordem inteligente pelo histórico',
      description: orderHistoryLoading
        ? 'Carregando os últimos envios...'
        : orderHistory.length > 0
          ? `Aprende com os últimos ${orderHistory.length} envios`
          : 'Disponível após o primeiro envio concluído',
      icon: <HistoryIcon />,
      recommended: false,
      disabled: orderHistoryLoading || orderHistory.length === 0
    },
    { value: 'valuation', title: 'Maior valoração', description: 'Do maior para o menor valor financeiro', icon: <ValuationIcon /> },
    { value: 'audience', title: 'Maior audiência', description: 'Do maior para o menor alcance de público', icon: <AudienceIcon /> },
    { value: 'tier', title: 'Prioridade por Tier', description: 'Tier 1 → Tier 2 → Tier 3', icon: <TierIcon /> }
  ];

  return (
    <PlatformDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <PlatformDialogTitle
        title="Gabarito de Envio"
        subtitle="Configure a ordem das seções e artigos"
        icon={<SortByAlphaIcon />}
        onClose={onClose}
      />

      <DialogContent sx={{ p: 3, bgcolor: 'background.default' }}>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={800}>Como deseja ordenar?</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Escolha uma regra para organizar os artigos dentro de cada classificação.
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.25 }}>
            {orderingOptions.map(option => {
              const selected = sortCriteria === option.value;
              return (
                <Paper
                  key={option.value}
                  component="button"
                  type="button"
                  disabled={option.disabled}
                  onClick={() => setSortCriteria(option.value)}
                  variant="outlined"
                  sx={{
                    position: 'relative',
                    display: 'flex',
                    gap: 1.25,
                    alignItems: 'flex-start',
                    width: '100%',
                    p: 1.5,
                    borderRadius: 2,
                    textAlign: 'left',
                    font: 'inherit',
                    color: 'text.primary',
                    cursor: option.disabled ? 'not-allowed' : 'pointer',
                    opacity: option.disabled ? 0.55 : 1,
                    borderColor: selected ? 'primary.main' : 'divider',
                    bgcolor: selected ? 'action.selected' : 'background.paper',
                    boxShadow: selected ? 2 : 0,
                    transition: 'all 0.18s ease',
                    '&:hover': option.disabled ? {} : { borderColor: 'primary.main', transform: 'translateY(-1px)' }
                  }}
                >
                  <Box sx={{ color: selected ? 'primary.main' : 'text.secondary', fontSize: '1.25rem', lineHeight: 1, display: 'flex' }}>
                    {option.icon}
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                      <Typography variant="body2" fontWeight={800}>{option.title}</Typography>
                      {option.recommended && !option.disabled && (
                        <Chip label="Recomendado" size="small" color="primary" sx={{ height: 18, fontSize: '0.6rem' }} />
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35, lineHeight: 1.3 }}>
                      {option.description}
                    </Typography>
                  </Box>
                  {selected && <CheckCircleIcon color="primary" sx={{ fontSize: 19 }} />}
                  {option.value === 'history' && orderHistoryLoading && <CircularProgress size={16} />}
                </Paper>
              );
            })}
          </Box>

          {sortCriteria === 'history' && (
            <Paper variant="outlined" sx={{ mt: 1.5, p: 1.5, borderRadius: 2, borderColor: 'primary.main', bgcolor: 'action.selected' }}>
              <Typography variant="caption" color="text.secondary">
                Preferências diretas do cliente têm prioridade. Fontes nunca comparadas são ordenadas por audiência, Tier, valoração e data.
              </Typography>
            </Paper>
          )}
          {sortCriteria === 'structural' && (
            <Paper variant="outlined" sx={{ mt: 1.5, p: 1.5, borderRadius: 2, borderColor: 'primary.main', bgcolor: 'action.selected' }}>
              <Typography variant="caption" color="text.secondary">
                Fontes conhecidas mantêm suas posições mesmo quando ficam ausentes em outros envios. Ao confirmar um Ajuste manual, a estrutura aprende a nova preferência sem remover as fontes ausentes.
              </Typography>
            </Paper>
          )}
        </Box>

        {/* CAMADA 1 — Ordem das Tags */}
        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Button
            fullWidth
            onClick={() => setShowTagOrder(current => !current)}
            sx={{ justifyContent: 'flex-start', p: 1.75, color: 'text.primary', textTransform: 'none' }}
          >
            <TagIcon color="primary" fontSize="small" />
            <Box sx={{ ml: 1, flex: 1, textAlign: 'left' }}>
              <Typography variant="body2" fontWeight={800}>Ajustar ordem das classificações</Typography>
              <Typography variant="caption" color="text.secondary">Opcional · {orderedTags.length} classificações</Typography>
            </Box>
            <ExpandMoreIcon sx={{ transform: showTagOrder ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
          </Button>
          <Box sx={{ display: showTagOrder ? 'block' : 'none', px: 2, pb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <Typography variant="subtitle2" fontWeight={700}>
              Ordem das Classificações
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Arraste ou use as setas para definir qual classificação aparece primeiro no email.
          </Typography>

          {orderedTags.length === 0 ? (
            <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 2, bgcolor: isDarkMode ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)' }}>
              <Typography color="text.secondary" variant="body2">
                Nenhuma tag configurada nesta marca. Crie tags para organizar sua newsletter.
              </Typography>
            </Paper>
          ) : (
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="tags-order">
                {(provided, snapshot) => (
                  <Box
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                      minHeight: 100,
                    }}
                  >
                    {orderedTags.map((tag, index) => (
                      <Draggable key={tag.id} draggableId={tag.id} index={index}>
                        {(provided, snapshot) => (
                          <Paper
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            elevation={snapshot.isDragging ? 12 : 0}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 2,
                              p: 1.5,
                              borderRadius: 2,
                              bgcolor: snapshot.isDragging 
                                ? (isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.02)')
                                : 'background.paper',
                              border: '1px solid',
                              borderColor: snapshot.isDragging ? 'primary.main' : 'divider',
                              transition: 'all 0.2s ease',
                              position: 'relative',
                              overflow: 'hidden',
                              '&:hover': {
                                borderColor: 'primary.light',
                                bgcolor: isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.01)',
                              },
                            }}
                          >
                            {/* Drag handle */}
                            <Box 
                              {...provided.dragHandleProps} 
                              sx={{ 
                                display: 'flex', 
                                color: 'text.disabled',
                                cursor: 'grab',
                                '&:active': { cursor: 'grabbing' }
                              }}
                            >
                              <DragIndicatorIcon fontSize="small" />
                            </Box>

                            {/* Número de ordem */}
                            <Avatar
                              sx={{
                                width: 28,
                                height: 28,
                                fontSize: '0.75rem',
                                fontWeight: 800,
                                bgcolor: snapshot.isDragging ? 'primary.main' : 'action.selected',
                                color: snapshot.isDragging ? 'primary.contrastText' : 'text.primary',
                                transition: 'all 0.2s ease',
                              }}
                            >
                              {index + 1}
                            </Avatar>

                            {/* Nome da tag */}
                            <Typography variant="body2" fontWeight={700} sx={{ flex: 1 }}>
                              {tag.name}
                            </Typography>

                            {/* Contagem */}
                            <Chip
                              label={`${getArticleCount(tag.id, tag.name)} art.`}
                              size="small"
                              variant="outlined"
                              sx={{
                                height: 20,
                                fontSize: '0.65rem',
                                fontWeight: 700,
                                borderColor: snapshot.isDragging ? 'rgba(255,255,255,0.4)' : 'divider',
                                color: getArticleCount(tag.id, tag.name) > 0 ? (snapshot.isDragging ? 'primary.contrastText' : 'primary.main') : 'text.disabled',
                                bgcolor: getArticleCount(tag.id, tag.name) > 0 && !snapshot.isDragging ? (isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)') : 'transparent',
                              }}
                            />

                            {/* Botões de mover */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0, ml: 1 }}>
                              <IconButton
                                size="small"
                                disabled={index === 0}
                                onClick={() => moveTag(index, -1)}
                                sx={{ p: 0.2, color: 'text.disabled', '&:hover': { color: 'primary.main' } }}
                              >
                                <ArrowUpIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                              <IconButton
                                size="small"
                                disabled={index === orderedTags.length - 1}
                                onClick={() => moveTag(index, 1)}
                                sx={{ p: 0.2, color: 'text.disabled', '&:hover': { color: 'primary.main' } }}
                              >
                                <ArrowDownIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Box>

                            {/* Indicador visual lateral */}
                            {snapshot.isDragging && (
                              <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, background: getPrimaryBackground() }} />
                            )}
                          </Paper>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </Box>
                )}
              </Droppable>
            </DragDropContext>
          )}
          </Box>
        </Paper>

      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 2, bgcolor: isDarkMode ? 'rgba(0,0,0,0.1)' : 'rgba(0,0,0,0.02)', borderTop: '1px solid', borderColor: 'divider' }}>
        <Button 
          onClick={onClose} 
          variant="text" 
          sx={{ color: 'text.secondary', fontWeight: 600 }}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleApply}
          variant="contained"
          startIcon={<CheckCircleIcon />}
          sx={{ 
            background: getPrimaryBackground(),
            color: 'primary.contrastText',
            fontWeight: 800, 
            px: 4,
            py: 1,
            borderRadius: 2,
            boxShadow: isPrimaryGradient ? '0 4px 14px rgba(0,0,0,0.25)' : undefined,
            '&:hover': {
              opacity: 0.9,
              transform: 'translateY(-1px)',
              boxShadow: isPrimaryGradient ? '0 6px 20px rgba(0,0,0,0.3)' : undefined,
            },
            transition: 'all 0.2s ease',
          }}
        >
          Aplicar Gabarito
        </Button>
      </DialogActions>
    </PlatformDialog>
  );
};

export default NewsletterOrderDialog;
